import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerDescription,
} from './ui/drawer';

interface RetirementNotificationProps {
  playerName: string;
  runs: number;
  retirementRuns: number;
  onConfirm: () => void;
  onCancel: () => void;
  onOverride: () => void;
}

export function RetirementNotification({
  playerName,
  runs,
  retirementRuns,
  onConfirm,
  onCancel,
  onOverride
}: RetirementNotificationProps) {
  return (
    <Drawer open={true} onOpenChange={onCancel}>
      <DrawerContent className="max-h-[85vh]">
        <div className="mx-auto w-full max-w-sm">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Retirement Required
            </DrawerTitle>
            <DrawerDescription>
              Player has exceeded the retirement run limit and must be retired.
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-4 p-4 pb-0">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                <strong>{playerName}</strong> has scored {runs} runs and must retire at {retirementRuns} runs according to the custom rules.
              </p>
            </div>
            <p className="text-sm text-gray-600">
              The batter will be marked as "retired not out" and a new batter will come to the crease.
            </p>
          </div>
          <DrawerFooter>
            <Button onClick={onConfirm} className="bg-orange-600 hover:bg-orange-700">
              Retire Batter
            </Button>
            <Button variant="outline" onClick={onOverride} className="border-orange-200 text-orange-700 hover:bg-orange-50">
              Override Rule
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface MaxBallsOverNotificationProps {
  overNumber: number;
  ballsInOver: number;
  maxBalls: number;
  isLastOver: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onOverride: () => void;
}

export function MaxBallsOverNotification({
  overNumber,
  ballsInOver,
  maxBalls,
  isLastOver,
  onConfirm,
  onCancel,
  onOverride
}: MaxBallsOverNotificationProps) {
  return (
    <Drawer open={true} onOpenChange={onCancel}>
      <DrawerContent className="max-h-[85vh]">
        <div className="mx-auto w-full max-w-sm">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2 text-blue-600">
              <AlertTriangle className="w-5 h-5" />
              Over Limit Reached
            </DrawerTitle>
            <DrawerDescription>
              {isLastOver 
                ? "This is the last over - normal cricket rules apply." 
                : "The maximum balls per over limit has been reached."
              }
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-4 p-4 pb-0">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Over {overNumber}</strong> has reached {ballsInOver} balls 
                {isLastOver 
                  ? ", but this is the last over so it must continue until 6 legal deliveries are bowled."
                  : ` and must end at ${maxBalls} balls according to the custom rules.`
                }
              </p>
            </div>
            <p className="text-sm text-gray-600">
              {isLastOver
                ? "The last over exemption means extras don't count towards the ball limit and the over continues until 6 legal deliveries are completed."
                : "The over will be marked as complete and the next over will begin with the strike rotated."
              }
            </p>
          </div>
          <DrawerFooter>
            <Button onClick={onConfirm} className="bg-blue-600 hover:bg-blue-700">
              {isLastOver ? "Continue Over" : "End Over"}
            </Button>
            <Button variant="outline" onClick={onOverride} className="border-blue-200 text-blue-700 hover:bg-blue-50">
              Override Rule
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}